import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Card,
  CardContent
} from '@mui/material';
import ScrollableTabs from '@components/mobile/ScrollableTabs';
import ResponsiveDataView from '@components/mobile/ResponsiveDataView';
import MobileFormDialog from '@components/mobile/MobileFormDialog';
import {
  ArrowBack,
  CheckCircle,
  Cancel,
  EventBusy,
  AccountBalance,
  Add
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { principalAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import EmptyState from '../../components/EmptyState';

const LEAVE_TYPES = ['casual', 'sick', 'earned'];

const ManageLeaves = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Leave Balances state
  const [balances, setBalances] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [balanceDialog, setBalanceDialog] = useState(false);
  const [balanceForm, setBalanceForm] = useState({
    teacher_id: '',
    leave_type: 'casual',
    total_leaves: '',
    academic_year: ''
  });

  // Leave Requests state
  const [requests, setRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [reviewDialog, setReviewDialog] = useState(false);
  const [reviewingRequest, setReviewingRequest] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const getCurrentAcademicYear = () => {
    const now = new Date();
    const year = now.getFullYear();
    return now.getMonth() >= 3 ? `${year}-${year + 1}` : `${year - 1}-${year}`;
  };

  const fetchBalances = useCallback(async () => {
    try {
      setLoading(true);
      const response = await principalAPI.getLeaveBalances({ academic_year: getCurrentAcademicYear() });
      setBalances(response.data.balances || []);
      setTeachers(response.data.teachers || []);
    } catch (error) {
      showSnackbar('Error fetching leave balances', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const response = await principalAPI.getLeaveRequests(params);
      setRequests(response.data.requests || []);
    } catch (error) {
      showSnackbar('Error fetching leave requests', 'error');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    if (activeTab === 0) fetchBalances();
    else fetchRequests();
  }, [activeTab, fetchBalances, fetchRequests]);

  // Group balances by teacher
  const getTeacherBalanceMap = () => {
    const map = {};
    balances.forEach(b => {
      if (!map[b.teacher_id]) {
        map[b.teacher_id] = {
          teacher: b.teacher,
          balances: {}
        };
      }
      map[b.teacher_id].balances[b.leave_type] = b;
    });
    return map;
  };

  const handleSetBalance = async () => {
    try {
      if (!balanceForm.teacher_id || !balanceForm.leave_type || balanceForm.total_leaves === '') {
        showSnackbar('Please fill all fields', 'error');
        return;
      }
      await principalAPI.setLeaveBalance({
        ...balanceForm,
        academic_year: balanceForm.academic_year || getCurrentAcademicYear()
      });
      showSnackbar('Leave balance updated successfully');
      setBalanceDialog(false);
      setBalanceForm({ teacher_id: '', leave_type: 'casual', total_leaves: '', academic_year: '' });
      fetchBalances();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error setting leave balance', 'error');
    }
  };

  const handleReview = async (status) => {
    try {
      await principalAPI.reviewLeaveRequest(reviewingRequest.id, {
        status,
        review_notes: reviewNotes
      });
      showSnackbar(`Leave request ${status} successfully`);
      setReviewDialog(false);
      setReviewingRequest(null);
      setReviewNotes('');
      fetchRequests();
      // Refresh balances so "Leave Balances" tab reflects the updated used count
      fetchBalances();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error reviewing request', 'error');
    }
  };

  const openReviewDialog = (request) => {
    setReviewingRequest(request);
    setReviewNotes('');
    setReviewDialog(true);
  };

  const getStatusChip = (status) => {
    const config = {
      pending: { color: 'warning', label: 'Pending' },
      approved: { color: 'success', label: 'Approved' },
      rejected: { color: 'error', label: 'Rejected' }
    };
    const c = config[status] || { color: 'default', label: status };
    return <Chip size="small" color={c.color} label={c.label} />;
  };

  if (loading && balances.length === 0 && requests.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  const teacherBalanceMap = getTeacherBalanceMap();
  // Get all teachers including those without balances
  const allTeachers = teachers.map(t => ({
    ...t,
    balances: teacherBalanceMap[t.id]?.balances || {}
  }));

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1.5, sm: 3 } }}>
      {/* Header */}
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={3}>
        <Typography variant="h4" fontWeight="bold">Leave Management</Typography>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/principal/dashboard')}>
          Back to Dashboard
        </Button>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3, p: 0.75 }}>
        <ScrollableTabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          TabIndicatorProps={{ sx: { display: 'none' } }}
          sx={{
            minHeight: 0,
            '& .MuiTabs-flexContainer': { gap: 1 },
            '& .MuiTab-root': {
              minHeight: 0,
              borderRadius: 999,
              textTransform: 'none',
              fontWeight: 600,
              px: 2.5,
              py: 1,
              color: 'primary.main',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
              '&:hover': {
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.2)
              },
              '&.Mui-selected': {
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': {
                  bgcolor: 'primary.dark'
                }
              }
            }
          }}
        >
          <Tab icon={<AccountBalance />} label="Leave Balances" iconPosition="start" />
          <Tab icon={<EventBusy />} label="Leave Requests" iconPosition="start" />
        </ScrollableTabs>
      </Paper>

      {/* Tab 0: Leave Balances */}
      {activeTab === 0 && (
        <Paper sx={{ p: 3 }}>
          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={2}>
            <Typography variant="h6">
              Teacher Leave Balances — {getCurrentAcademicYear()}
            </Typography>
            <Button variant="contained" startIcon={<Add />} onClick={() => {
              setBalanceForm({ teacher_id: '', leave_type: 'casual', total_leaves: '', academic_year: getCurrentAcademicYear() });
              setBalanceDialog(true);
            }}>
              Set Balance
            </Button>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : allTeachers.length === 0 ? (
            <EmptyState
              icon={AccountBalance}
              title="No teachers found in this school"
              dense
            />
          ) : (
            <ResponsiveDataView
              rows={allTeachers}
              keyExtractor={(teacher) => teacher.id}
              renderCard={(teacher) => (
                <Card variant="outlined" sx={{ mb: 1.5 }}>
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{teacher.user?.name || teacher.name || 'N/A'}</Typography>
                        <Typography variant="caption" color="text.secondary">{teacher.employee_id || 'N/A'}</Typography>
                      </Box>
                      <Tooltip title="Set balance for this teacher">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => {
                            setBalanceForm({
                              teacher_id: teacher.id,
                              leave_type: 'casual',
                              total_leaves: '',
                              academic_year: getCurrentAcademicYear()
                            });
                            setBalanceDialog(true);
                          }}
                        >
                          <Add fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      {LEAVE_TYPES.map(type => {
                        const bal = teacher.balances[type];
                        return (
                          <Box key={type} textAlign="center">
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {type.charAt(0).toUpperCase() + type.slice(1)}
                            </Typography>
                            {bal ? (
                              <Chip
                                size="small"
                                label={`${bal.used_leaves} / ${bal.total_leaves}`}
                                color={bal.used_leaves >= bal.total_leaves ? 'error' : 'default'}
                                variant="outlined"
                              />
                            ) : (
                              <Typography variant="body2" color="text.secondary">—</Typography>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </CardContent>
                </Card>
              )}
              table={
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Teacher</strong></TableCell>
                        <TableCell><strong>Employee ID</strong></TableCell>
                        {LEAVE_TYPES.map(type => (
                          <TableCell key={type} align="center">
                            <strong>{type.charAt(0).toUpperCase() + type.slice(1)}</strong>
                            <br />
                            <Typography variant="caption" color="text.secondary">Used / Total</Typography>
                          </TableCell>
                        ))}
                        <TableCell align="center"><strong>Actions</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {allTeachers.map(teacher => (
                        <TableRow key={teacher.id}>
                          <TableCell>{teacher.user?.name || teacher.name || 'N/A'}</TableCell>
                          <TableCell>{teacher.employee_id || 'N/A'}</TableCell>
                          {LEAVE_TYPES.map(type => {
                            const bal = teacher.balances[type];
                            return (
                              <TableCell key={type} align="center">
                                {bal ? (
                                  <Chip
                                    size="small"
                                    label={`${bal.used_leaves} / ${bal.total_leaves}`}
                                    color={bal.used_leaves >= bal.total_leaves ? 'error' : 'default'}
                                    variant="outlined"
                                  />
                                ) : (
                                  <Typography variant="body2" color="text.secondary">—</Typography>
                                )}
                              </TableCell>
                            );
                          })}
                          <TableCell align="center">
                            <Tooltip title="Set balance for this teacher">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => {
                                  setBalanceForm({
                                    teacher_id: teacher.id,
                                    leave_type: 'casual',
                                    total_leaves: '',
                                    academic_year: getCurrentAcademicYear()
                                  });
                                  setBalanceDialog(true);
                                }}
                              >
                                <Add />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              }
            />
          )}
        </Paper>
      )}

      {/* Tab 1: Leave Requests */}
      {activeTab === 1 && (
        <Paper sx={{ p: 3 }}>
          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} mb={2} gap={2}>
            <Typography variant="h6">Leave Requests</Typography>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : requests.length === 0 ? (
            <EmptyState
              icon={EventBusy}
              title="No leave requests found"
              description={statusFilter !== 'all' ? 'Try a different status filter.' : undefined}
              variant={statusFilter !== 'all' ? 'no-results' : 'empty'}
              dense
            />
          ) : (
            <ResponsiveDataView
              rows={requests}
              keyExtractor={(req) => req.id}
              renderCard={(req) => (
                <Card variant="outlined" sx={{ mb: 1.5 }}>
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <Typography variant="body2" fontWeight={600}>{req.teacher?.user?.name || 'N/A'}</Typography>
                      {getStatusChip(req.status)}
                    </Box>
                    <Box display="flex" gap={1} flexWrap="wrap" mb={0.5}>
                      <Chip size="small" label={req.leave_type.charAt(0).toUpperCase() + req.leave_type.slice(1)} variant="outlined" />
                      <Typography variant="body2" color="text.secondary">
                        {new Date(req.start_date).toLocaleDateString()} – {new Date(req.end_date).toLocaleDateString()} ({req.num_days}d)
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{req.reason}</Typography>
                    {req.status === 'pending' ? (
                      <Button size="small" variant="outlined" startIcon={<CheckCircle />} onClick={() => openReviewDialog(req)}>
                        Review
                      </Button>
                    ) : req.review_notes ? (
                      <Typography variant="caption" color="text.secondary">{req.review_notes}</Typography>
                    ) : null}
                  </CardContent>
                </Card>
              )}
              table={
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Teacher</strong></TableCell>
                        <TableCell><strong>Leave Type</strong></TableCell>
                        <TableCell><strong>From</strong></TableCell>
                        <TableCell><strong>To</strong></TableCell>
                        <TableCell align="center"><strong>Days</strong></TableCell>
                        <TableCell><strong>Reason</strong></TableCell>
                        <TableCell align="center"><strong>Status</strong></TableCell>
                        <TableCell align="center"><strong>Actions</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {requests.map(req => (
                        <TableRow key={req.id}>
                          <TableCell>{req.teacher?.user?.name || 'N/A'}</TableCell>
                          <TableCell>
                            <Chip size="small" label={req.leave_type.charAt(0).toUpperCase() + req.leave_type.slice(1)} variant="outlined" />
                          </TableCell>
                          <TableCell>{new Date(req.start_date).toLocaleDateString()}</TableCell>
                          <TableCell>{new Date(req.end_date).toLocaleDateString()}</TableCell>
                          <TableCell align="center">{req.num_days}</TableCell>
                          <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            <Tooltip title={req.reason}><span>{req.reason}</span></Tooltip>
                          </TableCell>
                          <TableCell align="center">{getStatusChip(req.status)}</TableCell>
                          <TableCell align="center">
                            {req.status === 'pending' ? (
                              <Box display="flex" gap={0.5} justifyContent="center">
                                <Tooltip title="Review">
                                  <IconButton size="small" color="primary" onClick={() => openReviewDialog(req)}>
                                    <CheckCircle />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                {req.review_notes || '—'}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              }
            />
          )}
        </Paper>
      )}

      {/* Set Balance Dialog */}
      <MobileFormDialog
        open={balanceDialog}
        onClose={() => setBalanceDialog(false)}
        title="Set Leave Balance"
        actions={
          <>
            <Button onClick={() => setBalanceDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSetBalance}>Save</Button>
          </>
        }
      >
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <FormControl fullWidth>
            <InputLabel>Teacher</InputLabel>
            <Select
              value={balanceForm.teacher_id}
              label="Teacher"
              onChange={(e) => setBalanceForm({ ...balanceForm, teacher_id: e.target.value })}
            >
              {teachers.map(t => (
                <MenuItem key={t.id} value={t.id}>
                  {t.user?.name || t.name || 'Unknown'} ({t.employee_id || 'N/A'})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Leave Type</InputLabel>
            <Select
              value={balanceForm.leave_type}
              label="Leave Type"
              onChange={(e) => setBalanceForm({ ...balanceForm, leave_type: e.target.value })}
            >
              {LEAVE_TYPES.map(type => (
                <MenuItem key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Total Leaves"
            type="number"
            fullWidth
            value={balanceForm.total_leaves}
            onChange={(e) => setBalanceForm({ ...balanceForm, total_leaves: e.target.value })}
            inputProps={{ min: 0 }}
          />
          <TextField
            label="Academic Year"
            fullWidth
            value={balanceForm.academic_year || getCurrentAcademicYear()}
            onChange={(e) => setBalanceForm({ ...balanceForm, academic_year: e.target.value })}
            placeholder="e.g. 2025-2026"
          />
        </Box>
      </MobileFormDialog>

      {/* Review Request Dialog */}
      <MobileFormDialog
        open={reviewDialog}
        onClose={() => setReviewDialog(false)}
        title="Review Leave Request"
        actions={
          <>
            <Button onClick={() => setReviewDialog(false)}>Cancel</Button>
            <Button variant="contained" color="error" startIcon={<Cancel />} onClick={() => handleReview('rejected')}>
              Reject
            </Button>
            <Button variant="contained" color="success" startIcon={<CheckCircle />} onClick={() => handleReview('approved')}>
              Approve
            </Button>
          </>
        }
      >
        {reviewingRequest && (
          <Box mt={1}>
            <Typography variant="body2" gutterBottom>
              <strong>Teacher:</strong> {reviewingRequest.teacher?.user?.name || 'N/A'}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Type:</strong> {reviewingRequest.leave_type.charAt(0).toUpperCase() + reviewingRequest.leave_type.slice(1)}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Dates:</strong> {new Date(reviewingRequest.start_date).toLocaleDateString()} — {new Date(reviewingRequest.end_date).toLocaleDateString()} ({reviewingRequest.num_days} day{reviewingRequest.num_days > 1 ? 's' : ''})
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Reason:</strong> {reviewingRequest.reason}
            </Typography>
            <TextField
              label="Review Notes (optional)"
              fullWidth
              multiline
              rows={3}
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              sx={{ mt: 2 }}
            />
          </Box>
        )}
      </MobileFormDialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ManageLeaves;
